import React, { useState, useEffect } from 'react';
import StableStreamingMessage from './StableStreamingMessage';

const TestStreamingIssue: React.FC = () => {
  const [content, setContent] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  
  // Simulate the exact content structure from the screenshots
  const thinkingContent = `<thinking>
I need to analyze the user's requirements for calculating deferred revenue:

The user wants to:
- Calculate 12 months of deferred revenue
- Keep columnar details for monthly revenue
- Start from July 2025

Looking at the uploaded file "Demo_1_input_deferred_revenue.csv", I'll need to:
- Parse the contract data
- Calculate monthly revenue recognition for each contract
- Create a waterfall schedule showing revenue by month
- Format it in a columnar layout

Key considerations:
- Date parsing (the dates are in object format)
- Handling contracts that start before, during, or after the Jul 2025 period
- Calculating the monthly allocation based on contract duration
- Different currencies and exchange rates
- Contracts with different end dates
</thinking>

## Understanding Your Requirements

### Data Analysis

**Filename:** Demo_1_input_deferred_revenue.csv

• **Contents:** Contract data with 1,200 active contracts containing financial and operational details
• **Key Revenue Fields:** Total_Contract_Value, Contract_Start_Date, Contract_End_Date, Revenue_Recognition_Method
• **Data Quality:** Very clean - no nulls in critical financial fields, all contracts are "Active" status

## Script Generation

Generating Python script based on the requirements analysis...

\`\`\`python
import pandas as pd
import numpy as np
from datetime import datetime, timedelta
from dateutil.relativedelta import relativedelta
import os

# Read the data
print("Loading contract data...")
df = pd.read_csv('Demo_1_input_deferred_revenue.csv')

# Convert date columns to datetime
print("Converting date columns...")
df['Contract_Start_Date'] = pd.to_datetime(df['Contract_Start_Date'])
df['Contract_End_Date'] = pd.to_datetime(df['Contract_End_Date'])

# Define the 12-month period starting from July 2025
start_date = datetime(2025, 7, 1)
end_date = start_date + relativedelta(months=12) - timedelta(days=1)  # June 30, 2026

# Create month columns for the waterfall
months = []
current_month = start_date
while current_month <= end_date:
    months.append(current_month.strftime('%b %Y'))
    current_month += relativedelta(months=1)

print(f"Creating revenue waterfall for period: {start_date.strftime('%B %Y')} to {end_date.strftime('%B %Y')}")

# Initialize the waterfall dataframe
revenue_waterfall = pd.DataFrame(index=df.index)
revenue_waterfall['Contract_ID'] = df['Contract_ID']
revenue_waterfall['Customer_Name'] = df['Customer_Name']
revenue_waterfall['Total_Contract_Value'] = df['Total_Contract_Value']
revenue_waterfall['Contract_Start_Date'] = df['Contract_Start_Date']
revenue_waterfall['Contract_End_Date'] = df['Contract_End_Date']
revenue_waterfall['Currency'] = df['Currency']
revenue_waterfall['Recognition_Method'] = df['Revenue_Recognition_Method']

# Initialize monthly columns
for month in months:
    revenue_waterfall[month] = 0.0

# Calculate monthly revenue for each contract
print("Calculating monthly revenue recognition...")
for idx, row in df.iterrows():
    contract_start = row['Contract_Start_Date']
    contract_end = row['Contract_End_Date']
    total_value = row['Total_Contract_Value']
    recognition_method = row['Revenue_Recognition_Method']
    
    # Calculate contract duration in days
    contract_duration = (contract_end - contract_start).days + 1
    
    # For straight-line recognition, calculate daily rate
    if recognition_method == 'Straight-Line':
        daily_rate = total_value / contract_duration
        
        # For each month in our reporting period
        current_month = start_date
        month_idx = 0
        while current_month <= end_date and month_idx < len(months):
            # Get the first and last day of the current month
            month_start = current_month
            month_end = min(current_month + relativedelta(months=1) - timedelta(days=1), end_date)
            
            # Find the overlap between contract period and current month
            overlap_start = max(contract_start, month_start)
            overlap_end = min(contract_end, month_end)
            
            # If there's an overlap, calculate revenue for this month
            if overlap_start <= overlap_end:
                overlap_days = (overlap_end - overlap_start).days + 1
                monthly_revenue = daily_rate * overlap_days
                revenue_waterfall.loc[idx, months[month_idx]] = monthly_revenue
            
            current_month += relativedelta(months=1)
            month_idx += 1

# Add total recognized column
revenue_waterfall['Total_Recognized'] = revenue_waterfall[months].sum(axis=1)

# Create summary by month
print("Creating monthly summary...")
monthly_summary = pd.DataFrame()
monthly_summary['Month'] = months
monthly_summary['Revenue'] = [revenue_waterfall[month].sum() for month in months]
monthly_summary['Contract_Count'] = [
    (revenue_waterfall[month] > 0).sum() for month in months
]

# Format currency values
for month in months:
    revenue_waterfall[month] = revenue_waterfall[month].round(2)
revenue_waterfall['Total_Recognized'] = revenue_waterfall['Total_Recognized'].round(2)
monthly_summary['Revenue'] = monthly_summary['Revenue'].round(2)

# Add cumulative revenue to summary
monthly_summary['Cumulative_Revenue'] = monthly_summary['Revenue'].cumsum()

# Create output Excel file with formatting
print("Creating formatted Excel output...")
output_filename = 'deferred_revenue_waterfall_jul2025.xlsx'
with pd.ExcelWriter(output_filename, engine='xlsxwriter') as writer:
    # Write detailed waterfall
    revenue_waterfall.to_excel(writer, sheet_name='Revenue Waterfall', index=False)
    
    # Write monthly summary
    monthly_summary.to_excel(writer, sheet_name='Monthly Summary', index=False)
    
    # Get workbook and worksheets
    workbook = writer.book
    waterfall_sheet = writer.sheets['Revenue Waterfall']
    summary_sheet = writer.sheets['Monthly Summary']
    
    # Define formats
    header_format = workbook.add_format({
        'bold': True,
        'text_wrap': True,
        'valign': 'top',
        'bg_color': '#1F4E79',
        'font_color': 'white',
        'border': 1
    })
    
    currency_format = workbook.add_format({
        'num_format': '#,##0.00',
        'border': 1
    })
    
    date_format = workbook.add_format({
        'num_format': 'yyyy-mm-dd',
        'border': 1
    })
    
    # Format waterfall sheet
    waterfall_sheet.set_column('A:A', 15)  # Contract ID
    waterfall_sheet.set_column('B:B', 30)  # Customer Name
    waterfall_sheet.set_column('C:C', 18)  # Total Contract Value
    waterfall_sheet.set_column('D:E', 15)  # Date columns
    waterfall_sheet.set_column('F:F', 10)  # Currency
    waterfall_sheet.set_column('G:G', 20)  # Recognition Method
    
    # Format monthly columns
    start_col = 7  # H column
    for i in range(len(months)):
        waterfall_sheet.set_column(start_col + i, start_col + i, 12, currency_format)
    waterfall_sheet.set_column(start_col + len(months), start_col + len(months), 15, currency_format)
    
    # Format summary sheet
    summary_sheet.set_column('A:A', 12)  # Month
    summary_sheet.set_column('B:B', 15, currency_format)  # Revenue
    summary_sheet.set_column('C:C', 15)  # Contract Count
    summary_sheet.set_column('D:D', 18, currency_format)  # Cumulative Revenue
    
    # Add conditional formatting to highlight high revenue months
    summary_sheet.conditional_format('B2:B13', {
        'type': 'data_bar',
        'bar_color': '#4472C4'
    })

print(f"\\nRevenue waterfall saved to: {output_filename}")
print(f"\\nSummary Statistics:")
print(f"- Total contracts processed: {len(df)}")
print(f"- Total revenue recognized in period: $" + f"{monthly_summary['Revenue'].sum():,.2f}")
print(f"- Average monthly revenue: $" + f"{monthly_summary['Revenue'].mean():,.2f}")
print(f"- Peak month: {monthly_summary.loc[monthly_summary['Revenue'].idxmax(), 'Month']} ($" + f"{monthly_summary['Revenue'].max():,.2f})")
\`\`\``;
  
  // Function to simulate streaming
  const simulateStreaming = () => {
    setIsStreaming(true);
    setContent('');
    
    let currentIndex = 0;
    const chunkSize = 50; // Characters per chunk
    
    const streamInterval = setInterval(() => {
      if (currentIndex >= thinkingContent.length) {
        clearInterval(streamInterval);
        setIsStreaming(false);
        console.log('Streaming complete. Total length:', thinkingContent.length);
        console.log('Total lines:', thinkingContent.split('\n').length);
        return;
      }
      
      const nextChunk = thinkingContent.substring(currentIndex, currentIndex + chunkSize);
      setContent(prev => {
        const newContent = prev + nextChunk;
        
        // Log every 1000 characters
        if (newContent.length % 1000 < chunkSize) {
          console.log(`Content length: ${newContent.length}, Lines: ${newContent.split('\n').length}`);
        }
        
        return newContent;
      });
      
      currentIndex += chunkSize;
    }, 10); // Stream every 10ms
  };
  
  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h2>Test Streaming Issue</h2>
      <button 
        onClick={simulateStreaming}
        style={{ 
          padding: '10px 20px', 
          marginBottom: '20px',
          background: '#FF6600',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer'
        }}
      >
        Start Streaming Test
      </button>
      
      <div style={{ 
        background: '#f9fafb', 
        padding: '20px', 
        borderRadius: '8px',
        border: '1px solid #e5e7eb'
      }}>
        <StableStreamingMessage content={content} isStreaming={isStreaming} />
      </div>
      
      <div style={{ marginTop: '20px', fontSize: '12px', color: '#666' }}>
        <p>Content Length: {content.length} characters</p>
        <p>Line Count: {content.split('\n').length} lines</p>
        <p>Streaming: {isStreaming ? 'Yes' : 'No'}</p>
      </div>
    </div>
  );
};

export default TestStreamingIssue;